import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Nav, Image } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../api/apiClient';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import LocationAutocomplete from "../components/LocationAutocomplete";

export default function Profile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    old_password: '',
    new_password: '',
    new_password_confirmation: '',
    photo: '',
    phone: '',
    address: '',
    gender: '',
    city: '',
    postal_code: '',
    age: '',
    is_super: '',
    type: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { currentUser } = useAuth();
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [activeTab, setActiveTab] = useState('personal');

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await authAPI.getProfile();
        console.log(response);
        if (response.success) {
          const userData = response.data.data || response.data;
          setFormData(prev => ({
            ...prev,
            is_super: userData.is_super || '',
            name: userData.name || '',
            email: userData.email || '',
            phone: userData.phone || '',
            address: userData.address || '',
            gender: userData.gender || '',
            city: userData.city || '',
            postal_code: userData.postal_code || '',
            age: userData.age || '',
            photo: userData.photo || '',
            type: userData.type || '',
          }));
          if (userData.photo) {
            setPhotoPreview(userData.photo);
          }
        } else {
          setError(t('failed_to_fetch_profile'));
        }
      } catch (err) {
        setError(t('failed_to_fetch_profile'));
      }
    };
    fetchProfile();
  }, [t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLocationChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
      ...(e.lat && { [`${e.target.name}_lat`]: e.lat }),
      ...(e.lng && { [`${e.target.name}_lng`]: e.lng })
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const resetFileInput = () => {
    setPhotoFile(null);
    setPhotoPreview(formData.photo || '');
    if (fileRef.current) {
      fileRef.current.value = '';
    }
  };

  const handlePersonalSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        age: formData.age,
        address: formData.address,
        gender: formData.gender,
        city: formData.city,
        postal_code: formData.postal_code,
      };
      const response = await authAPI.updateProfile(data);
      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: t('profile_updated'),
          text: t('profile_updated_success'),
          timer: 2000,
          showConfirmButton: false
        });
        setFormData(prev => ({ ...prev, ...response.data.data }));
      } else {
        setError(response.message || t('failed_to_update_profile'));
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: response.message || t('failed_to_update_profile'),
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (err) {
      setError(t('failed_to_update_profile'));
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: t('failed_to_update_profile'),
        timer: 2000,
        showConfirmButton: false
      });
      console.error('Error updating profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.new_password !== formData.new_password_confirmation) {
      setError(t('passwords_do_not_match'));
      setLoading(false);
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: t('passwords_do_not_match'),
        timer: 2000,
        showConfirmButton: false
      });
      return;
    }

    try {
      const data = {
        old_password: formData.old_password,
        new_password: formData.new_password,
        new_password_confirmation: formData.new_password_confirmation
      };
      const response = await authAPI.changePassword(data);
      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: t('password_updated'),
          text: t('password_updated_success'),
          timer: 2000,
          showConfirmButton: false
        });
        setFormData(prev => ({ 
          ...prev, 
          old_password: '', 
          new_password: '', 
          new_password_confirmation: '' 
        }));
      } else {
        setError(response.message || t('failed_to_update_password'));
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: response.message || t('failed_to_update_password'),
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (err) {
      setError(t('failed_to_update_password'));
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: t('failed_to_update_password'),
        timer: 2000,
        showConfirmButton: false
      });
      console.error('Error updating password:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePictureSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!photoFile) {
      setError(t('no_file_selected'));
      setLoading(false);
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: t('no_file_selected'),
        timer: 2000,
        showConfirmButton: false
      });
      return;
    }

    try {
      const fd = new FormData();
      fd.append('photo', photoFile);

      console.log('Uploading photo...');
      const response = await authAPI.updateProfile(fd);
      console.log('Photo upload response:', response);

      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: t('picture_updated'),
          text: t('picture_updated_success'),
          timer: 2000,
          showConfirmButton: false
        });
        const newPhoto = response.data.data?.photo || formData.photo;
        setFormData(prev => ({ ...prev, photo: newPhoto }));
        setPhotoPreview(newPhoto);
        setPhotoFile(null);
        if (fileRef.current) fileRef.current.value = '';
      } else {
        setError(response.message || t('failed_to_update_picture'));
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: response.message || t('failed_to_update_picture'),
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (err) {
      setError(t('failed_to_update_picture'));
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: t('failed_to_update_picture'),
        timer: 2000,
        showConfirmButton: false
      });
      console.error('Error updating picture:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid className="py-4">
      <div className="dashboard-header">
        <h4 className="mb-0">{t('profile')}</h4>
      </div>
      <div className="py-4">

        <div className="d-flex justify-content-between">
          <h5 className="mb-4 dashboard-title">{t('profile_settings')}</h5>
          <h5 className="mb-0"> {formData.is_super == 1 ? 'Super Admin' : 'Admin'}</h5>
        </div>

        <Nav
          variant="tabs"
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-4"
        >
          <Nav.Item>
            <Nav.Link eventKey="personal">{t('personal_info')}</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="picture">{t('photo')}</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="password">{t('change_password')}</Nav.Link>
          </Nav.Item>
        </Nav>

        {error && <Alert variant="danger">{error}</Alert>}

        {activeTab === 'personal' && (
          <Card className="shadow mb-4">
            <Card.Body className="p-5">
              <Form onSubmit={handlePersonalSubmit}>
                <Form.Group className="mb-3" controlId="formName">
                  <Form.Label>{t('name')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formEmail">
                  <Form.Label>{t('email')}</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3" controlId="formPhone">
                  <Form.Label>{t('phone')}</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3" controlId="formAddress">
                  <Form.Label>{t('address')}</Form.Label>
                  <Form.Control
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3" controlId="formAge">
                  <Form.Label>{t('age')}</Form.Label>
                  <Form.Control
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3" controlId="formGender">
                  <Form.Label>{t('gender')}</Form.Label>
                  <div>
                    <Form.Check
                      inline
                      type="radio"
                      label={t('Male')}
                      name="gender"
                      value="Male"
                      checked={formData.gender === "Male"}
                      onChange={handleChange}
                    />
                    <Form.Check
                      inline
                      type="radio"
                      label={t('Female')}
                      name="gender"
                      value="Female"
                      checked={formData.gender === "Female"}
                      onChange={handleChange}
                    />
                  </div>
                </Form.Group>

                <Form.Group className="mb-3" controlId="formPostalCode">
                  <Form.Label>{t('postal_code')}</Form.Label>
                  <LocationAutocomplete
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleLocationChange}
                  />
                </Form.Group>

                <Button
                  disabled={loading}
                  className="w-100"
                  variant="primary"
                  type="submit"
                >
                  {t('update')}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        )}

        {activeTab === 'picture' && (
          <Card className="shadow mb-4">
            <Card.Body className="p-5">
              <div className="text-center mb-3">
                <p>{t('current_picture')}</p>
                {photoPreview ? (
                  <div>
                    <Image
                      src={photoPreview}
                      roundedCircle
                      style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                      className="mb-2"
                    />
                    {photoFile && (
                      <div className="mt-2">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={resetFileInput}
                          disabled={loading}
                        >
                          {t('cancel')}
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <p>{t('no_picture')}</p>
                )}
              </div>
              <Form onSubmit={handlePictureSubmit}>
                <Form.Group className="mb-3" controlId="formFile">
                  <Form.Label>{t('new_picture')}</Form.Label>
                  <Form.Control
                    type="file"
                    ref={fileRef}
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                </Form.Group>
                <Button
                  disabled={loading || !photoFile}
                  className="w-100"
                  variant="primary"
                  type="submit"
                >
                  {loading ? t('updating') : t('update')}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        )}

        {activeTab === 'password' && (
          <Card className="shadow mb-4">
            <Card.Body className="p-5">
              <Form onSubmit={handlePasswordSubmit}>
                <Form.Group className="mb-3" controlId="formOldPassword">
                  <Form.Label>{t('old_password')}</Form.Label>
                  <Form.Control
                    type="password"
                    name="old_password"
                    value={formData.old_password}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formNewPassword">
                  <Form.Label>{t('new_password')}</Form.Label>
                  <Form.Control
                    type="password"
                    name="new_password"
                    value={formData.new_password}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="formNewPasswordConfirmation">
                  <Form.Label>{t('confirm_password')}</Form.Label>
                  <Form.Control
                    type="password"
                    name="new_password_confirmation"
                    value={formData.new_password_confirmation}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
                <Button
                  disabled={loading}
                  className="w-100"
                  variant="primary"
                  type="submit"
                >
                  {t('update')}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        )}
      </div>
    </Container>
  );
}